/*
  HL SHOPABILITY P1 — one cart API for the whole page.

  WHY THIS EXISTS
  ---------------
  A plain page load on this store issues five to seven calls to the cart API
  before the shopper has touched anything. Most of them are one app polling:
  the Analyzely GA4 embed runs `setInterval(function(){ fetch('/cart.js') },
  3000)`, so every open tab asks Shopify for the cart twenty times a minute
  for as long as it stays open. Selleasy and the DECO badge extension each
  add one more on load.

  Shopify rate-limits the cart endpoints. Past that limit it stops answering
  with JSON and answers with an HTML "Verifying your connection" page under
  HTTP 429 — which is exactly what `.then(r => r.json())` reports as a failed
  fetch. That is the shape of the 22,910 cart-API errors in the logs, and it
  is reproducible: driving the endpoints at the rate the page already drives
  them puts this store into the challenge within a minute.

  WHAT IT DOES
  ------------
  Wraps window.fetch and XMLHttpRequest before any app script runs, so every
  cart read on the page — ours and theirs — goes through one door:

    * concurrent reads share a single in-flight request;
    * a read is served from a short cache instead of hitting the network, so
      a 3-second poll costs nothing;
    * the cache is dropped the moment anything writes to the cart, so a poll
      still sees an add or a remove immediately;
    * a read that fails, or that comes back as the HTML challenge page, is
      retried once with backoff before the caller is told anything went
      wrong — so a single throttled response is no longer a dead drawer.

  It changes no call site. Anything that already calls /cart.js keeps working
  and simply gets answered faster and more often from memory.
*/
(function () {
  'use strict';

  if (window.HLCart) return;

  var STALE_MS   = 15000;  /* a passive read may be this old before we refetch */
  var RETRY_MS   = 700;    /* backoff before the single retry                  */
  var FAIL_MS    = 10000;  /* after a failure, stop asking for this long       */
  var THROTTLE_MS = 20000; /* after a 429, stop asking for longer still        */
  var CART_READ  = /\/cart\.js(\?|$)/;
  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js|$|\?)/;

  var origFetch = window.fetch && window.fetch.bind(window);
  if (!origFetch) return;

  var cachedText = null;
  var cachedAt   = 0;

  /* ---- header badge -----------------------------------------------------
     Every cart the page learns about - a read, or the body of a write -
     passes through setCached(). That is the one place that knows the live
     item count, so the header badge is painted from here rather than from
     each caller: an add from the PDP, quick-add, Selleasy or the drawer's
     own +/- all land in the same place. The badge elements are rendered by
     Liquid on every page (hidden at zero), so there is always something to
     paint. Also fires `hl:cart` on document for anything else that wants
     the parsed cart. */
  var lastCount = null;
  function paintBadge(cart) {
    if (!cart || typeof cart.item_count !== 'number') return;
    if (cart.item_count === lastCount) return;
    lastCount = cart.item_count;
    var els = document.querySelectorAll('.cart-link__count, .hl-bag-count');
    for (var i = 0; i < els.length; i++) {
      els[i].textContent = String(cart.item_count);
      if (cart.item_count > 0) els[i].removeAttribute('hidden'); else els[i].setAttribute('hidden', '');
    }
    try { document.dispatchEvent(new CustomEvent('hl:cart', { detail: cart })); } catch (e) {}
  }
  function setCached(text) {
    cachedText = text;
    cachedAt = Date.now();
    failedAt = 0;
    try { paintBadge(JSON.parse(text)); } catch (e) {}
  }
  var inflight   = null;
  var failedAt   = 0;
  var failCooldown = FAIL_MS;

  /* ---- request shape helpers -------------------------------------------
     Callers pass a string, a URL, or a Request object. The Request-object
     branch matters: apps on this store (Globo, Selleasy) use it, and a
     typeof-string check alone misses every one of them. */
  function describe(args) {
    var input = args[0];
    var init = args[1] || {};
    var url = '';
    var method = (init.method || 'GET').toUpperCase();
    try {
      if (typeof input === 'string') {
        url = input;
      } else if (input && typeof input === 'object') {
        if (typeof input.url === 'string') url = input.url;
        else url = String(input);
        if (input.method) method = String(input.method).toUpperCase();
      }
    } catch (e) {}
    return { url: url, method: method };
  }

  function looksLikeJson(text) {
    var t = (text || '').replace(/^\uFEFF/, '').trim();
    return t.charAt(0) === '{' || t.charAt(0) === '[';
  }

  function jsonResponse(text) {
    return new Response(text, {
      status: 200,
      headers: { 'Content-Type': 'application/json; charset=utf-8' },
    });
  }

  /* ---- the one real read ------------------------------------------------ */
  function fetchCartText(attempt) {
    return origFetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.text().then(function (text) {
          /* A throttled or challenged response is HTML, not JSON. Treat it
             as a failure so the caller is never handed an HTML string to
             JSON.parse - which is how this shows up in the logs today. */
          if (!r.ok || !looksLikeJson(text)) {
            var e = new Error('cart.js ' + r.status);
            e.status = r.status;
            /* 429 means "you are asking too often". Retrying immediately is
               the one response guaranteed to make it worse, so a throttle is
               never retried and buys a longer cooldown than a dropped
               connection does. */
            e.throttled = r.status === 429 || r.status === 430;
            throw e;
          }
          return text;
        });
      })
      .catch(function (err) {
        if (attempt || (err && err.throttled)) throw err;
        return new Promise(function (resolve, reject) {
          setTimeout(function () { fetchCartText(1).then(resolve, reject); }, RETRY_MS);
        });
      });
  }

  function readCart(force) {
    var fresh = cachedText !== null && (Date.now() - cachedAt) < STALE_MS;
    if (!force && fresh) return Promise.resolve(cachedText);
    if (inflight) return inflight;
    /* NEGATIVE CACHING. The failure this whole file exists to contain is a
       rate limit, and the worst thing to do to a rate limit is to answer it
       with more requests. Without this, a cart API that is refusing us turns
       a 3-second poll into a 3-second poll PLUS a retry each time — twice
       the traffic, at exactly the moment the store is asking for less. So a
       failure closes the door for FAIL_MS: callers get the last known cart
       if we have one, and a rejection if we do not, with nothing put on the
       wire either way. */
    if ((Date.now() - failedAt) < failCooldown) {
      if (cachedText !== null) return Promise.resolve(cachedText);
      return Promise.reject(new Error('cart.js unavailable (cooling down)'));
    }
    inflight = fetchCartText(0)
      .then(function (text) {
        setCached(text);
        inflight = null;
        return text;
      })
      .catch(function (err) {
        failedAt = Date.now();
        failCooldown = (err && err.throttled) ? THROTTLE_MS : FAIL_MS;
        inflight = null;
        throw err;
      });
    return inflight;
  }

  function invalidate() {
    cachedText = null;
    cachedAt = 0;
    /* A write is a fresh reason to ask, so it also lifts the cooldown. */
    failedAt = 0;
  }

  /* ---- public surface --------------------------------------------------- */
  window.HLCart = {
    /* Resolves with the parsed cart. `force` skips the cache but still
       shares an in-flight request. Rejects only after the retry failed. */
    get: function (force) {
      return readCart(!!force).then(function (text) { return JSON.parse(text); });
    },
    /* Last cart we successfully read, or null. Lets a caller paint
       something truthful while a refresh is in the air. */
    peek: function () {
      try { return cachedText ? JSON.parse(cachedText) : null; } catch (e) { return null; }
    },
    invalidate: invalidate,
  };

  /* ---- fetch ------------------------------------------------------------ */
  window.fetch = function () {
    var info = describe(arguments);
    if (CART_WRITE.test(info.url)) {
      invalidate();
      return origFetch.apply(this, arguments).then(function (r) {
        /* Every cart write answers with the WHOLE updated cart. Reading it
           from the write's own response is free, and it means the next
           reader is served from memory instead of the page paying for a
           fresh /cart.js immediately after every change - which was two of
           the three reads a first page load used to cost, because an app
           writes a session attribute to the cart on load. */
        try {
          r.clone().text().then(function (text) {
            if (looksLikeJson(text) && text.indexOf('"item_count"') > -1) {
              setCached(text);
            }
          }, function () {});
        } catch (e) { invalidate(); }
        return r;
      });
    }
    if (info.method === 'GET' && CART_READ.test(info.url)) {
      return readCart(false).then(jsonResponse);
    }
    return origFetch.apply(this, arguments);
  };

  /* ---- XMLHttpRequest ---------------------------------------------------
     Older app bundles still use XHR. We only need the write half here: an
     XHR read is rare and passing it through costs one request, whereas
     missing an XHR write would leave the cache stale after a real change. */
  var origOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    try {
      if (CART_WRITE.test(String(url))) {
        this.addEventListener('loadend', function () {
          invalidate();
          /* An app adding by XHR answers with the whole cart as well. */
          try {
            var t = this.responseText;
            if (this.status === 200 && looksLikeJson(t) && t.indexOf('"item_count"') > -1) setCached(t);
          } catch (e) {}
        });
        invalidate();
      }
    } catch (e) {}
    return origOpen.apply(this, arguments);
  };

  /* A cart can change in another tab, or while this one was backgrounded. */
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) invalidate();
  });
  /* Only a BFCACHE restore is a reason to distrust the cache. The plain
     pageshow that follows every ordinary load fires just after the first
     read, so invalidating on it threw away a cart we had only just fetched
     and made the page pay for a second one. */
  window.addEventListener('pageshow', function (e) { if (e && e.persisted) invalidate(); });
}());
